import Component from '../classes/Component'
import Globe from 'globe.gl'
import * as countriesData from '../../data/countries.min.json'
import GlobeImage from '../../images/earth-blue-marble.jpg'
import * as THREE from 'three'
import ScrollingMarquee from './ScrollingMarquee'
import { getWindowHeightMinusNav, mapEach } from '../utils/dom'
import gsap from 'gsap/all'
import Scrollbar from 'smooth-scrollbar'
import SplitText from 'gsap/SplitText'
import { wrap } from '../utils/text'
import ScrollTrigger from 'gsap/ScrollTrigger'

export default class extends Component {
  constructor () {
    super({
      classes: {},
      element: 'body',
      elements: {
        globe: '.global-story__globe-content__globe',
        stats: '.global-story__stats',
        globeFirstRowMarquee: '[data-globe-first-row-marquee]',
        globeSecondRowMarquee: '[data-globe-second-row-marquee]',
        globeRows: '[data-scrolling-marquee-inner]',
        modalContainer: '.global-story__modal',
        modal: '.global-story__modal__modal',
        modalFigureTitle: '.global-story__modal__modal figure p',
        modalImage: '.global-story__modal__modal figure img',
        modalFigureScreen: '.global-story__modal__modal figure div',
        modalClose: '.global-story__modal__modal__close',
        modalCountry: '.global-story__modal__modal__content__top h2 .word-container .word',
        modalEstablishedYear: '.global-story__modal__modal__content__top ul li:nth-child(1)',
        modalRegisteredChildren: '.global-story__modal__modal__content__top ul li:nth-child(2)',
        modalMumsBabies: '.global-story__modal__modal__content__top ul li:nth-child(3)',
        modalChurchPartners: '.global-story__modal__modal__content__top ul li:nth-child(4)',
        modalLearnMore: '.global-story__modal__modal__content a',
        modalContent: '.global-story__modal__modal__content',
        instructions: '.global-story__instructions',
        instructionsClick: '.global-story__instructions__text__click',
        instructionsDrag: '.global-story__instructions__text__drag',
        instructionsClickIcon: '.global-story__instructions__icon__click',
        instructionsDragIcon: '.global-story__instructions__icon__drag'
      }
    })

    this.COUNTRY_ROTATOR_SPEED = 40
    this.REVEAL_ANIMATION_SPEED = 0.2

    this.rotationY = 0
    this.visible = false
    this.scrollbar = Scrollbar.get(document.querySelector('.scroll-container'))
    this.isModalAnimating = false

    this.create()
  }

  create () {
    this.globe = new Globe({
      animateIn: false
    })(this.elements.globe)
      .backgroundColor('rgba(0, 0, 0, 0)')
      .atmosphereColor('lightskyblue')
      .globeImageUrl(GlobeImage)
      .width(window.innerWidth)
      .height(getWindowHeightMinusNav())
      .polygonsData(countriesData.features)
      .polygonCapColor(feat => 'rgba(255, 255, 255, 0)')
      .polygonStrokeColor(() => 'rgba(255, 255, 255, 1)')
      .polygonSideColor(() => 'rgba(0, 100, 0, 0.05)')
      .onPolygonClick((polygon, event) => {
        this.onClick(polygon)
      })

    this.globe.controls().enableZoom = false
    this.globe.camera().zoom = 1.2

    setTimeout(() => { // wait for scene to be populated (asynchronously)
      const directionalLight = this.globe.scene().children.find(obj3d => obj3d.type === 'DirectionalLight')
      const ambientLight = new THREE.AmbientLight(0xFFFFFF, 0.3)
      this.globe.scene().remove(directionalLight)
      this.globe.scene().add(ambientLight)
      // if (DetectionManager.isMobileOrTablet()) {
      //   this.globe.controls().enabled = false
      // }
      ScrollTrigger.refresh()
    }, 1000)

    this.createGlobeMarquee()
    this.elements.modalClose.addEventListener('click', this.closeModal)

    this.instructionsClickWords = new SplitText(this.elements.instructionsClick, {
      type: 'words',
      wordsClass: 'word'
    })
    this.instructionsClickWords.words.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })

    this.instructionsDragWords = new SplitText(this.elements.instructionsDrag, {
      type: 'words',
      wordsClass: 'word'
    })
    this.instructionsDragWords.words.forEach((word) => {
      const div = document.createElement('div')
      div.classList.add('word-container')
      wrap(word, div)
    })

    // Create instructions timeline
    gsap.timeline({
      repeat: -1
    })
      .to(this.elements.instructionsClickIcon, {
        opacity: 1,
        ease: 'power2.in',
        duration: 0.4
      })
      .to(this.instructionsClickWords.words, {
        y: 0,
        duration: 0.4,
        stagger: 0.005,
        ease: 'power2.in'
      }, '-=0.4')
      .to({}, {
        duration: 3
      })
      .to(this.elements.instructionsClickIcon, {
        opacity: 0,
        duration: 0.4,
        ease: 'power2.out'
      })
      .to(this.instructionsClickWords.words, {
        y: '-100%',
        duration: 0.4,
        stagger: 0.005,
        ease: 'power2.out'
      }, '-=0.4')
      .to(this.elements.instructionsDragIcon, {
        opacity: 1,
        duration: 0.4,
        ease: 'power2.in'
      })
      .to(this.instructionsDragWords.words, {
        y: 0,
        duration: 0.4,
        stagger: 0.005,
        ease: 'power2.in'
      }, '-=0.4')
      .to({}, {
        duration: 3
      })
      .to(this.elements.instructionsDragIcon, {
        opacity: 0,
        duration: 0.4,
        ease: 'power2.out'
      })
      .to(this.instructionsDragWords.words, {
        y: '-100%',
        duration: 0.4,
        stagger: 0.005,
        ease: 'power2.out'
      }, '-=0.4')
  }

  createGlobeMarquee () {
    const SPEED_FACTOR = 10
    const WIDTH = this.elements.globeFirstRowMarquee[0].clientWidth
    this.marquees = []
    mapEach(this.elements.globeFirstRowMarquee, item => {
      this.marquees.push(new ScrollingMarquee(item, null, 'left', WIDTH / SPEED_FACTOR))
    })
    mapEach(this.elements.globeSecondRowMarquee, item => {
      this.marquees.push(new ScrollingMarquee(item, null, 'right', WIDTH / SPEED_FACTOR))
    })
    mapEach(this.elements.stats, item => {
      this.marquees.push(this.statsMarquee = new ScrollingMarquee(item, {
        inner: '.global-story__stats__content',
        row: 'ul',
        item: 'li'
      }, 'left', 70))
    })
  }

  // Events.

  onClick (polygon) {
    // Hide instructions
    gsap.to(this.elements.instructions, {
      opacity: 0,
      scale: 0.5,
      ease: 'power4.inOut',
      pointerEvents: 'none'
    })
    // Check if delivering program
    if (polygon.properties.registered_children) {
      // Temporarily disable scrollbar
      gsap.set(this.elements.modalLearnMore, {
        display: 'inline-block'
      })
      gsap.set([this.elements.modalFigureTitle], {
        opacity: 0
      })
      gsap.set(this.elements.modalImage, {
        opacity: 0,
        scale: 1.5
      })
      this.elements.modalCountry.textContent = polygon.properties.name
      this.elements.modalEstablishedYear.querySelector('div:nth-child(2)').textContent = polygon.properties.established_year
      this.elements.modalRegisteredChildren.querySelector('div:nth-child(2)').textContent = polygon.properties.registered_children.toLocaleString()
      if (polygon.properties.show_local_label) {
        this.elements.modalChurchPartners.querySelector('div:nth-child(1)').textContent = 'Local Partners'
      } else {
        this.elements.modalChurchPartners.querySelector('div:nth-child(1)').textContent = 'Church Partners'
      }
      this.elements.modalChurchPartners.querySelector('div:nth-child(2)').textContent = polygon.properties.church_partners.toLocaleString()
      this.elements.modalMumsBabies.querySelector('div:nth-child(2)').textContent = polygon.properties.mums_babies.toLocaleString()
      this.elements.modalLearnMore.href = polygon.properties.url
      const image = new window.Image()
      image.src = polygon.properties.image
      image.onload = () => {
        gsap.to(this.elements.modalImage, {
          opacity: 1,
          scale: 1,
          ease: 'expo.out',
          duration: 1
        })
      }
      this.elements.modalImage.src = polygon.properties.image
      // Set which to animate in
      this.elements.modalEstablishedYear.dataset.animate = true
      this.elements.modalRegisteredChildren.dataset.animate = true
      this.elements.modalChurchPartners.dataset.animate = true
      this.elements.modalMumsBabies.dataset.animate = true
    } else {
      gsap.set(this.elements.modalImage, {
        opacity: 0,
        scale: 1.5
      })
      const genericImageUrl = '//images.ctfassets.net/c2js6hi58pvj/2Oi9k64tRumWsi2USs0wWo/878c06a1cc9276757c56a3d27ab25f99/WhyCompassionBanner.jpg'
      const image = new window.Image()
      image.src = genericImageUrl
      image.onload = () => {
        gsap.to(this.elements.modalImage, {
          opacity: 1,
          scale: 1,
          ease: 'expo.out',
          duration: 1
        })
      }
      this.elements.modalImage.src = genericImageUrl
      gsap.set(this.elements.modalCountry, {
        y: '100%'
      })
      gsap.set(this.elements.modalLearnMore, {
        display: 'none'
      })
      gsap.set(this.elements.modalFigureTitle, {
        opacity: 1
      })
      this.elements.modalCountry.textContent = polygon.properties.name
      this.elements.modalEstablishedYear.querySelector('div:nth-child(2)').textContent = polygon.properties.established_year

      // Set which to animate in
      this.elements.modalEstablishedYear.dataset.animate = true
      this.elements.modalRegisteredChildren.dataset.animate = false
      this.elements.modalChurchPartners.dataset.animate = false
      this.elements.modalMumsBabies.dataset.animate = false
    }

    gsap.set(this.elements.modalContent.querySelectorAll('[data-animate=false]'), {
      display: 'none'
    })
    gsap.set(this.elements.modalContent.querySelectorAll('[data-animate=true]'), {
      opacity: 0,
      display: 'flex',
      y: '2rem'
    })

    this.openModal()
  }

  openModal () {
    if (this.isModalAnimating) {
      return
    }
    this.isModalAnimating = true

    gsap.timeline({
      onComplete: () => {
        this.isModalAnimating = false
      }
    })
      .to(this.elements.modal, {
        y: 0,
        duration: 0.4,
        ease: 'power4.inOut'
      }, 0)
      .to(this.elements.modalCountry, {
        y: 0,
        duration: 0.4,
        ease: 'power4.inOut'
      }, 0)
      .to(this.elements.modalContent.querySelectorAll('[data-animate=true]'), {
        opacity: 1,
        y: 0,
        stagger: 0.05,
        duration: 0.6,
        ease: 'power4.out'
      }, 0.2)
  }

  closeModal () {
    if (this.isModalAnimating) {
      return
    }
    gsap.timeline({
    })
      .to(this.elements.modal, {
        y: '100%',
        duration: 0.3,
        ease: 'power4.inOut'
      }, 0)
      .to(this.elements.modalCountry, {
        y: '100%',
        duration: 0.3,
        ease: 'power4.inOut'
      }, 0.2)
  }

  onResize () {
    this.globe.width([window.innerWidth])
    this.globe.height([getWindowHeightMinusNav()])
    // if (DetectionManager.isMobileOrTablet()) {
    //   this.globe.controls().enabled = false
    // }
  }

  update () {
    this.globe.scene().rotation.y = this.rotationY
  }
}
